import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  SocialDistance as SocialDistanceIcon,
  AltRouteOutlined as AltRouteOutlinedIcon,
  SpeedOutlined,
  Logout as LogoutIcon,
  AccountCircle,
  AccountCircleOutlined
} from '@mui/icons-material';
import LeftNavigation from '../components/navigation/LeftNavigation';
import { useAuth } from '../contexts/AuthContext';
import { ReactComponent as Logo } from '../assets/tivra_logo.svg';
import ProfileDialog from '../components/ProfileDialog';
import { useTeam } from '../contexts/TeamContext';
import ResizeErrorBoundary from '../components/common/ResizeErrorBoundary';
import FeedbackWidget from '../components/common/FeedbackWidget';


const drawerWidth = 324;
const headerHeight = 72;



export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { setSelectedMember } = useTeam();
  const [openDialog, setOpenDialog] = useState(false);

  // Add ResizeObserver error handler
  useEffect(() => {
    const resizeHandler = () => {
      const resizeObserverError = console.error;
      console.error = (...args) => {
        if (args[0]?.includes?.('ResizeObserver') || 
            args[0]?.message?.includes?.('ResizeObserver')) {
          return;
        }
        resizeObserverError.apply(console, args);
      };
    };

    window.addEventListener('resize', resizeHandler);
    resizeHandler(); // Initial call

    return () => {
      window.removeEventListener('resize', resizeHandler);
      console.error = console.error; // Restore original error handler
    };
  }, []);

  // Initialize tab based on current route
  const [selectedTab, setSelectedTab] = useState(() => {
    if (location.pathname.includes('/app/meetings')) return 0;
    if (location.pathname.includes('/app/career')) return 1;
    if (location.pathname.includes('/app/performance')) return 2;
    return 0;
  });

  // Update selected tab when route changes
  useEffect(() => {
    if (location.pathname.includes('/app/meetings')) setSelectedTab(0);
    else if (location.pathname.includes('/app/career')) setSelectedTab(1);
    else if (location.pathname.includes('/app/performance')) setSelectedTab(2);
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    switch (newValue) {
      case 0:
        navigate('/app/meetings');
        break;
      case 1:
        navigate('/app/career');
        break;
      case 2:
        navigate('/app/performance');
        break;
      default:
        break;
    }
  };

  const handleLogout = async () => {
    try {
      setSelectedMember(null);
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleProfileClick = () => {
    setOpenDialog(true);
  };

  return (
    <ResizeErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ display: 'flex', bgcolor: '#FFFFFF', minHeight: '100vh' }}>
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                bgcolor: '#F8F8F8',
                border: 'none',
                boxShadow: 'none',
                zIndex: 1
              },
            }}
          >
            <Box sx={{
              p: 2,
              borderBottom: '1px solid #E0E0E0',
              height: '72px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Link to="/">
                <Logo width="240px" height="50px" />
              </Link>
            </Box>
            <LeftNavigation />
          </Drawer>

          <Box
            sx={{
              flexGrow: 1,
              bgcolor: '#FFFFFF',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1)',
                zIndex: 2
              }
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                pl: 3,
                height: headerHeight,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                  height: '100%',
                  flexGrow: 1,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    height: headerHeight,
                    px: 2,
                  },
                }}
              >
                <Tab
                  icon={<SocialDistanceIcon />}
                  iconPosition="start"
                  label="1:1 Meetings"
                />
                <Tab
                  icon={<AltRouteOutlinedIcon />}
                  iconPosition="start"
                  label="Career planning"
                />
                <Tab
                  icon={<SpeedOutlined />}
                  iconPosition="start"
                  label="Performance appraisal"
                />
              </Tabs>

              <Box sx={{ pr: 6 }}>
                <IconButton onClick={handleProfileClick}>
                  <AccountCircleOutlined />
                </IconButton>
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: '#666666',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Box>
            </Box>

            <ProfileDialog open={openDialog} onClose={() => setOpenDialog(false)} />
            <Outlet />
          </Box>
        </Box>
        <FeedbackWidget />
      </Box>
    </ResizeErrorBoundary>
  );
} 